import React from 'react'
import { UserService } from '@duck/api-definitions'
import { Badge, Button } from '@duck/ui'
import { getStatusBadgeColor, formatStatus, type Column } from './utils'
import { InvitationsTableSkeleton } from './InvitationsTableSkeleton'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import { useOrganizations } from '@/storeHooks/useOrganizations'
import { toast } from 'sonner'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'

interface InvitationsTableProps {
  invitations: UserService.Invitation[]
  totalInvitations: number
  visibleColumns: Column[]
  sortField: keyof UserService.Invitation
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof UserService.Invitation) => void
  onInvitationClick?: (invitation: UserService.Invitation) => void
  onInviteClick?: (invitation: UserService.Invitation) => void
  loadingMode?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isPageLoading?: boolean
}

export const InvitationsTable: React.FC<InvitationsTableProps> = ({
  invitations,
  totalInvitations,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onInvitationClick,
  onInviteClick,
  loadingMode = false,
  hasMore = false,
  onLoadMore,
  isPageLoading = false,
}) => {
  const { t } = useTar()
  const { idToNameMap } = useOrganizations()

  const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
    hasMore,
    onLoadMore,
    isLoading: isPageLoading,
    disabled: loadingMode,
    itemCount: invitations.length,
    offsetFromEnd: 10,
  })

  const cellRenderers: Partial<Record<keyof UserService.Invitation, (invitation: UserService.Invitation) => React.ReactNode>> = {
    id: (invitation) => (
      <span className="text-xs font-mono text-muted-foreground">
        {invitation.id}
      </span>
    ),
    email: (invitation) => (
      <p className="text-sm font-medium text-foreground">
        {invitation.email}
      </p>
    ),
    firstName: (invitation) => (
      <p className="text-sm text-foreground">
        {invitation.firstName}
      </p>
    ),
    lastName: (invitation) => (
      <p className="text-sm text-foreground">
        {invitation.lastName}
      </p>
    ),
    accessGroups: (invitation) => (
      <div className="max-w-xs">
        {invitation.accessGroups && invitation.accessGroups.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {invitation.accessGroups.map((group, index) => (
              <Badge key={index} variant="outline" className="bg-accent/10 text-accent border-accent/20">
                {group}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noGroups')}</span>
        )}
      </div>
    ),
    status: (invitation) => {
      const handleCopyLink = async () => {
        if (invitation.token) {
          const invitationLink = `${window.location.origin}/?token=${invitation.token}`
          try {
            await navigator.clipboard.writeText(invitationLink)
            toast.success(t('common.linkCopied'))
          } catch (error) {
            console.error('Failed to copy link:', error)
            toast.error(t('common.failedToCopyLink') + '\n' + invitationLink, {
              duration: 15000,
            })
          }
        }
      }

      return (
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(invitation.status)}`}>
            {t(formatStatus(invitation.status, !!invitation.token))}
          </span>
          {invitation.token && invitation.status === UserService.InvitationStatusEnum.Active && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyLink()
              }}
              className="text-primary underline hover:text-primary/80 text-xs cursor-pointer"
            >
              {t('common.copyLink')}
            </button>
          )}
        </div>
      )
    },
    validFor: (invitation) => (
      <span className="text-sm text-muted-foreground">
        {invitation.validFor} {t('common.days')}
      </span>
    ),
    inviterId: (invitation) => (
      <span className="text-xs font-mono text-muted-foreground">
        {invitation.inviterId || t('common.na')}
      </span>
    ),
    inviterName: (invitation) => (
      <span className="text-sm text-foreground">
        {invitation.inviterName || t('common.na')}
      </span>
    ),
    registeredUserId: (invitation: UserService.Invitation) => (
      <span className="text-sm text-muted-foreground">
        {invitation.registeredUserId || t('common.notRegistered')}
      </span>
    ),
    organizationId: (invitation) => {
      const orgName = invitation.organizationId
        ? idToNameMap.get(invitation.organizationId)
        : null
      return (
        <span className="text-sm text-foreground">
          {orgName || invitation.organizationId || t('common.na')}
        </span>
      )
    },
    createdAt: (invitation) => (
      <RelativeDateTime
        isoDateString={invitation.createdAt}
        className="text-sm text-muted-foreground"
      />
    ),
    updatedAt: (invitation) => (
      <RelativeDateTime
        isoDateString={invitation.updatedAt}
        className="text-sm text-muted-foreground"
      />
    ),
    expiresAt: (invitation) => (
      <RelativeDateTime
        isoDateString={invitation.expiresAt}
        className="text-sm text-muted-foreground"
      />
    ),
    customBody: (invitation) => (
      <div className="text-sm text-muted-foreground">
        {invitation.customBody}
      </div>
    ),
    role: (invitation) => (
      <span className="text-sm text-muted-foreground">
        {invitation.role}
      </span>
    ),
  }

  const renderCellContent = (invitation: UserService.Invitation, columnKey: Column['key']) => {
    if (columnKey === 'actions') {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onInviteClick?.(invitation)
          }}
        >
          {t('invitations.invite')}
        </Button>
      )
    }
    const renderer = cellRenderers[columnKey as keyof UserService.Invitation]
    return renderer ? renderer(invitation) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-foreground">
          {t('invitations.allInvitations')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {loadingMode ? t('common.loading') : t('common.XRecords', { count: invitations.length })}
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                >
                  {column.sortable && column.key !== 'actions' ? (
                    <button
                      onClick={() => onSort(column.key as keyof UserService.Invitation)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      <span>{t(column.label)}</span>
                      {sortField === column.key && (
                        <span className="text-accent">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    t(column.label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <InvitationsTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
            ) : (
              invitations.map((invitation, index) => {
                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex

                return (
                  <tr
                    key={invitation.id}
                    ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                    className={`hover:bg-secondary/5 ${onInvitationClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onInvitationClick?.(invitation)}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCellContent(invitation, column.key)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && !loadingMode && (
        <div className="flex items-center justify-center py-4">
          {isPageLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
            </div>
          )}
        </div>
      )}

      {!loadingMode && invitations.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('invitations.noInvitations')}</p>
        </div>
      )}
    </div>
  )
}
